import type { Language } from '@eformulieren/engine'
import { Check, ChevronDown, X } from 'lucide-react'
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type Ref,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { inLanguage } from '../../language/text.js'
import type { FieldLink } from '../FieldShell.js'
import { foldForSearch } from './richtext/find.js'

/**
 * Een keuzelijst die je doorzoekt: de combobox voor een `dropdown` of `multiselect` met veel opties
 * (R-166, G047, R-15, R-139, R-169).
 *
 * **Vanaf meer dan tien opties** (`SEARCH_FROM`). Daaronder blijft de gewone keuzelijst: een korte
 * lijst overzie je in één blik, en de `select` van het platform is op elk apparaat vertrouwd. Bij een
 * lange lijst moet de invuller scrollen en zoeken naar wat hij al weet; typen is dan sneller. Radio en
 * aanvinklijst blijven wat ze zijn: daar heeft de inrichter gekozen dat elke optie zichtbaar staat.
 *
 * **Het patroon is de "editable combobox with list autocomplete" van de WAI-ARIA Authoring
 * Practices**, met wat GOV.UK accessible-autocomplete en Headless UI erbij leren (de bronnen staan in
 * ontwerp 6.3). De focus blijft in het invoervak en `aria-activedescendant` wijst de optie aan; pijl
 * omlaag en omhoog lopen rond, Home en End gaan naar de eerste en de laatste, Enter kiest, Escape
 * sluit. Het aantal treffers gaat vertraagd naar een statusregio, zoals bij GOV.UK, zodat een
 * schermlezer niet bij elke letter een getal hoort.
 *
 * **Dezelfde waarde als de gewone keuzelijst.** Opgeslagen wordt de `waarde` van de optie, nooit het
 * label en nooit de getypte tekst: wie typt en weggaat zonder te kiezen, houdt zijn keuze en verliest
 * alleen de zoektekst. Daarom ook wist Escape nooit een keuze en haalt Backspace in een leeg vak
 * geen keuze weg (R-165); dat doet alleen de knop die het zegt. Regels en controles zien dus niets
 * anders dan voorheen.
 *
 * **Het vak volgt de stand zolang er niet wordt getypt.** Kiest een medeschrijver iets anders, of
 * wisselt de taal (R-144), dan staat het nieuwe label er meteen; wie op dat moment typt, houdt zijn
 * zoektekst tot hij kiest of weggaat.
 */

/** Vanaf dit aantal opties wordt een keuzelijst doorzoekbaar: meer dan tien. */
export const SEARCH_FROM = 11

export interface ChoiceOption {
  readonly waarde: string
  readonly label: { readonly nl: string; readonly en: string }
}

export function needsSearch(options: readonly ChoiceOption[]): boolean {
  return options.length >= SEARCH_FROM
}

interface Hit {
  readonly option: ChoiceOption
  /** De plek in de volle lijst, voor een id dat bij filteren niet verschuift. */
  readonly index: number
  /** Gevonden op de code en niet op het label: dan staat de code erbij, anders is de treffer een raadsel. */
  readonly viaCode: boolean
}

/** De opties die de zoektekst raken, op label of code, los van hoofdletters en accenten. */
function filterOptions(options: readonly ChoiceOption[], query: string, language: Language): Hit[] {
  const needle = foldForSearch(query.trim())
  return options.flatMap((option, index) => {
    if (needle === '') return [{ option, index, viaCode: false }]
    const onLabel = foldForSearch(inLanguage(option.label, language)).includes(needle)
    const onCode = foldForSearch(option.waarde).includes(needle)
    return onLabel || onCode ? [{ option, index, viaCode: !onLabel }] : []
  })
}

export interface ComboboxProps {
  readonly koppeling: FieldLink
  readonly taal: Language
  /** Het label van het veld, als naam van de lijst. */
  readonly naam: string
  readonly opties: readonly ChoiceOption[]
  readonly meerdere: boolean
  readonly gekozen: readonly (string | number)[]
  /** Wat er in het vak staat als er niet wordt getypt: het label van de keuze, of leeg. */
  readonly rusttekst: string
  readonly onKies: (waarde: string) => void
  /** Alleen bij één keuze: de keuze leegmaken. Een eigen knop, want leeg maken is een handeling. */
  readonly onWis?: () => void
  readonly invoerRef?: Ref<HTMLInputElement>
}

/** Hoe lang het aantal treffers wacht voordat een schermlezer het hoort. */
const ANNOUNCE_AFTER_MS = 500

export function Combobox({
  koppeling: link,
  taal: language,
  naam: name,
  opties: options,
  meerdere: multiple,
  gekozen: chosen,
  rusttekst: restText,
  onKies: onChoose,
  onWis: onClear,
  invoerRef,
}: ComboboxProps): ReactNode {
  const words = WORDS[language]
  /** De zoektekst, of `null` als er niet wordt getypt: dan staat de rusttekst in het vak. */
  const [query, setQuery] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  /** De aangewezen optie, op haar waarde: dan blijft ze aangewezen als de lijst onder haar filtert. */
  const [activeValue, setActiveValue] = useState<string | undefined>(undefined)
  const baseId = useId()
  const listId = `${baseId}-lijst`
  const hintId = `${baseId}-hint`
  const input = useRef<HTMLInputElement | null>(null)
  const root = useRef<HTMLDivElement | null>(null)
  const selectedOnFocus = useRef(false)

  const hits = useMemo(() => filterOptions(options, query ?? '', language), [options, query, language])
  const activeIndex = hits.findIndex((hit) => hit.option.waarde === activeValue)
  const optionId = (index: number): string => `${baseId}-optie-${String(index)}`
  const isChosen = (value: string): boolean => chosen.includes(value)

  const show = (where: 'eerste' | 'laatste' | 'gekozen' | 'geen', list: readonly Hit[] = hits): void => {
    setOpen(true)
    const picked = list.find((hit) => isChosen(hit.option.waarde))
    const target =
      where === 'laatste' ? list.at(-1) : where === 'gekozen' ? (picked ?? list[0]) : where === 'eerste' ? list[0] : undefined
    setActiveValue(target?.option.waarde)
  }

  const close = (): void => {
    setOpen(false)
    setQuery(null)
  }

  const move = (by: number): void => {
    if (hits.length === 0) return
    const from = activeIndex === -1 ? (by > 0 ? -1 : 0) : activeIndex
    const next = (((from + by) % hits.length) + hits.length) % hits.length
    setActiveValue(hits[next]?.option.waarde)
  }

  const choose = (value: string): void => {
    onChoose(value)
    setQuery(null)
    if (multiple) {
      // Open laten voor de volgende keuze, met deze optie nog aangewezen.
      setActiveValue(value)
    } else {
      setOpen(false)
    }
  }

  const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>): void => {
    switch (event.key) {
      case 'ArrowDown':
        if (event.altKey) show('geen')
        else if (!open) show('gekozen')
        else move(1)
        break
      case 'ArrowUp':
        if (!open) show('laatste')
        else move(-1)
        break
      case 'Home':
      case 'End':
        // Met de lijst dicht gaan Home en End gewoon naar het begin of eind van de tekst.
        if (!open || hits.length === 0) return
        setActiveValue((event.key === 'Home' ? hits[0] : hits.at(-1))?.option.waarde)
        break
      case 'Enter': {
        // Nooit het omliggende formulier versturen.
        event.preventDefault()
        const hit = hits[activeIndex]
        if (open && hit !== undefined) choose(hit.option.waarde)
        return
      }
      case 'Escape':
        // Sluiten en de zoektekst laten vallen, nooit de keuze wissen (R-165). Is er niets te sluiten,
        // dan hoort een dialoog eromheen de Escape gewoon.
        if (!open && query === null) return
        event.stopPropagation()
        close()
        break
      case 'Tab':
        close()
        return
      default:
        return
    }
    event.preventDefault()
  }

  // De aangewezen optie in beeld houden in een lijst die scrolt.
  useEffect(() => {
    if (!open || activeIndex === -1) return
    const hit = hits[activeIndex]
    if (hit === undefined) return
    document.getElementById(optionId(hit.index))?.scrollIntoView({ block: 'nearest' })
    // `optionId` hangt alleen van `baseId` af.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeIndex, hits])

  const message = !open ? '' : hits.length === 0 ? words.geen : words.gevonden(hits.length)
  const [announced, setAnnounced] = useState('')
  useEffect(() => {
    const timer = window.setTimeout(() => setAnnounced(message), ANNOUNCE_AFTER_MS)
    return () => window.clearTimeout(timer)
  }, [message])

  const setInput = (element: HTMLInputElement | null): void => {
    input.current = element
    if (typeof invoerRef === 'function') invoerRef(element)
    else if (invoerRef !== undefined && invoerRef !== null) (invoerRef as { current: HTMLInputElement | null }).current = element
  }

  const activeHit = hits[activeIndex]
  const showClearChoice = !multiple && onClear !== undefined && chosen.length > 0
  const showClearQuery = multiple && query !== null && query !== ''

  return (
    <div
      ref={root}
      className="ef-zoekkeuze"
      onBlur={(event) => {
        // Alleen als de focus de combobox echt verlaat, niet bij een stap naar de wisknop.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) close()
      }}
    >
      <div className="ef-zoekkeuze__veld">
        <input
          ref={setInput}
          type="text"
          className="ef-invoer ef-zoekkeuze__invoer"
          id={link.id}
          role="combobox"
          aria-labelledby={link['aria-labelledby']}
          aria-describedby={[link['aria-describedby'], hintId].filter((part) => part !== undefined).join(' ')}
          aria-invalid={link['aria-invalid']}
          aria-required={link['aria-required']}
          aria-expanded={open}
          aria-controls={open && hits.length > 0 ? listId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={open && activeHit !== undefined ? optionId(activeHit.index) : undefined}
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          placeholder={multiple ? words.plaatshouderMeer : words.plaatshouder}
          value={query ?? restText}
          onChange={(event) => {
            const text = event.target.value
            setQuery(text)
            // De eerste treffer aangewezen, zodat Enter meteen kiest (GOV.UK: `autoselect`).
            show('eerste', filterOptions(options, text, language))
          }}
          // Bij binnenkomen staat het label geselecteerd, zodat wie typt het vervangt en niet aanvult.
          // De mouseup na een klik zou die selectie weer opheffen, vandaar de vlag.
          onFocus={(event) => {
            if (query !== null) return
            event.target.select()
            selectedOnFocus.current = true
          }}
          onMouseUp={(event) => {
            if (!selectedOnFocus.current) return
            selectedOnFocus.current = false
            event.preventDefault()
          }}
          onClick={() => {
            if (!open) show('gekozen')
          }}
          onKeyDown={onKeyDown}
        />
        <span className="ef-zoekkeuze__knoppen">
          {showClearChoice || showClearQuery ? (
            <button
              type="button"
              className="ef-zoekkeuze__wis"
              aria-label={showClearChoice ? words.wisKeuze : words.wisZoektekst}
              title={showClearChoice ? words.wisKeuze : words.wisZoektekst}
              onClick={() => {
                if (showClearChoice) onClear?.()
                setQuery(null)
                setOpen(false)
                input.current?.focus()
              }}
            >
              <X aria-hidden="true" size={ICON_SIZE} />
            </button>
          ) : null}
          <span className="ef-zoekkeuze__pijl" aria-hidden="true">
            <ChevronDown size={ICON_SIZE} />
          </span>
        </span>
      </div>

      {open && hits.length > 0 ? (
        <ul
          className="ef-zoekkeuze__lijst"
          id={listId}
          role="listbox"
          aria-label={name}
          {...(multiple ? { 'aria-multiselectable': true } : {})}
        >
          {hits.map((hit) => {
            const selected = isChosen(hit.option.waarde)
            return (
              <li
                key={hit.option.waarde}
                id={optionId(hit.index)}
                role="option"
                aria-selected={selected}
                className={
                  hit === activeHit ? 'ef-zoekkeuze__optie ef-zoekkeuze__optie--actief' : 'ef-zoekkeuze__optie'
                }
                // De focus blijft in het invoervak, ook bij een klik.
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActiveValue(hit.option.waarde)}
                onClick={() => {
                  choose(hit.option.waarde)
                  input.current?.focus()
                }}
              >
                <span className="ef-zoekkeuze__vink" aria-hidden="true">
                  {selected ? <Check size={ICON_SIZE} /> : null}
                </span>
                <span className="ef-zoekkeuze__label">{inLanguage(hit.option.label, language)}</span>
                {hit.viaCode ? (
                  <>
                    {' '}
                    <span className="ef-zoekkeuze__code">{hit.option.waarde}</span>
                  </>
                ) : null}
              </li>
            )
          })}
        </ul>
      ) : null}
      {open && hits.length === 0 ? <p className="ef-zoekkeuze__leeg">{words.geen}</p> : null}

      <span className="ef-sr-only" id={hintId}>
        {multiple ? words.hintMeer : words.hint}
      </span>
      <span className="ef-sr-only" role="status">
        {announced}
      </span>
    </div>
  )
}

const ICON_SIZE = 18

const WORDS = {
  nl: {
    plaatshouder: 'Kies of typ om te zoeken',
    plaatshouderMeer: 'Typ om te zoeken en toe te voegen',
    hint: 'Typ om de lijst te doorzoeken. Met pijl omlaag opent u de lijst, met Enter kiest u.',
    hintMeer:
      'Typ om de lijst te doorzoeken. Met pijl omlaag opent u de lijst, met Enter kiest u een optie of haalt u haar weg. U kunt meer opties kiezen.',
    gevonden: (count: number) => (count === 1 ? '1 optie gevonden.' : `${String(count)} opties gevonden.`),
    geen: 'Geen opties gevonden. Pas uw zoektekst aan.',
    wisKeuze: 'Wis de keuze',
    wisZoektekst: 'Wis de zoektekst',
  },
  en: {
    plaatshouder: 'Choose or type to search',
    plaatshouderMeer: 'Type to search and add',
    hint: 'Type to search the list. Arrow down opens the list, Enter chooses.',
    hintMeer: 'Type to search the list. Arrow down opens the list, Enter chooses or removes an option. You can choose more than one.',
    gevonden: (count: number) => (count === 1 ? '1 option found.' : `${String(count)} options found.`),
    geen: 'No options found. Try different search text.',
    wisKeuze: 'Clear the choice',
    wisZoektekst: 'Clear the search text',
  },
} as const
